import {
  buildNutrientCards,
  buildSafetySummary,
  filterLogsByDate,
  filterLogsForLastDays,
  getLocalDateKey,
  sumNutrients,
} from "./v2DashboardCalculations";
import { getLogsForDate } from "./v2FoodLogUtils";
import { formatServing } from "./v2FoodUtils";
import {
  loadV2ApprovedTips,
  loadV2FoodLogs,
  loadV2FoodsUnified,
  loadV2KnowledgeItems,
  loadV2Settings,
} from "./v2Storage";
import { answerV2AppGuideQuestion } from "./v2AppGuide";

const BLOCKED_WRITE_RESPONSE =
  "I can help you find the food, but food logging must be done manually from the Log Food page.";
const BLOCKED_WRITE_RESPONSE_AR =
  "يمكنني مساعدتك في العثور على الطعام، لكن تسجيل الطعام يجب أن يتم يدويًا من صفحة تسجيل الطعام.";
const URGENT_HEALTH_RESPONSE =
  "If you have chest pain, trouble breathing, fainting, severe weakness, or another urgent health concern, contact local emergency medical services now. I cannot diagnose symptoms or make emergency care decisions.";
const NOT_CONNECTED_MESSAGE =
  "The assistant is not connected right now. You can still use food search, dashboard summaries, and history tools.";

const FOOD_SEARCH_PREFIXES = [
  "search for",
  "search",
  "find",
  "look up",
  "show me",
  "show",
  "nutrients for",
  "nutrition for",
  "فتش عن",
  "ابحث عن",
  "دور على",
  "دور عن",
];

const KNOWLEDGE_PREFIXES = [
  "what does the knowledge base say about",
  "what does knowledge base say about",
  "search knowledge base for",
  "search knowledge for",
  "show approved tips about",
  "knowledge base",
  "knowledge",
  "approved tips",
  "tips about",
  "قاعدة المعرفة",
  "نصائح",
];

const ARABIC_FOOD_ALIASES = Object.freeze({
  تفاح: "apple",
  التفاح: "apple",
  تفاحة: "apple",
  رز: "rice",
  أرز: "rice",
  ارز: "rice",
  دجاج: "chicken",
  حليب: "milk",
  جبنة: "cheese",
  جبن: "cheese",
  خيار: "cucumber",
  تبولة: "tabouleh",
  التبولة: "tabouleh",
  موز: "banana",
  موزة: "banana",
});

const NUTRIENT_DETAILS = Object.freeze({
  potassium: { field: "potassium_mg", unit: "mg", label: "potassium", labelAr: "البوتاسيوم" },
  phosphorus: { field: "phosphorus_mg", unit: "mg", label: "phosphorus", labelAr: "الفوسفور" },
  sodium: { field: "sodium_mg", unit: "mg", label: "sodium", labelAr: "الصوديوم" },
  protein: { field: "protein_g", unit: "g", label: "protein", labelAr: "البروتين" },
});

export function getReadOnlyAssistantFallbackMessage() {
  return NOT_CONNECTED_MESSAGE;
}

export function buildBlockedWriteResponse(language = "en") {
  return language === "ar" ? BLOCKED_WRITE_RESPONSE_AR : BLOCKED_WRITE_RESPONSE;
}

export function buildUrgentHealthResponse() {
  return URGENT_HEALTH_RESPONSE;
}

export function buildAssistantContext({ isGuest = false, settings } = {}) {
  const currentSettings = settings || loadV2Settings();
  const foodLogs = loadV2FoodLogs({ isGuest });
  const todayKey = getLocalDateKey();
  const todayLogs = filterLogsByDate(foodLogs, todayKey);
  const todayTotals = sumNutrients(todayLogs);
  const nutrientCards = buildNutrientCards(todayTotals, currentSettings.daily_targets || {});

  return {
    foods: loadV2FoodsUnified({ isGuest }),
    foodLogs,
    todayKey,
    todayLogs,
    todayTotals,
    dailyTargets: currentSettings.daily_targets || {},
    nutrientCards,
    safetySummary: buildSafetySummary(nutrientCards),
    knowledgeItems: loadV2KnowledgeItems({ includeInactive: false }),
    approvedTips: loadV2ApprovedTips(),
  };
}

export function searchAssistantFoods(query = "", foods = [], { limit = 6 } = {}) {
  const terms = normalizeSearchTerms(extractFoodSearchTerm(query));
  if (!terms.length) return [];

  return foods
    .map((food) => ({ food, score: scoreFood(food, terms) }))
    .filter((item) => item.score > 0)
    .sort((a, b) => b.score - a.score || getFoodName(a.food).localeCompare(getFoodName(b.food)))
    .slice(0, limit)
    .map((item) => item.food);
}

export function answerFoodSearch(query = "", context = {}) {
  const language = getResponseLanguage(query);
  const foods = context.foods || [];
  const searchTerm = extractFoodSearchTerm(query);
  const requestedNutrient = getRequestedNutrient(query);
  const mentionedFoods = findMentionedFoods(query, foods);

  if (!searchTerm && /available foods?|foods available|food list|شو في|أطعمة|اطعمة/i.test(query)) {
    return answerAvailableFoods(context, { language });
  }

  if (mentionedFoods.length >= 2) {
    return answerFoodComparison(mentionedFoods, requestedNutrient, language);
  }

  if (mentionedFoods.length === 1 && isDirectFoodQuestion(query)) {
    return answerSingleFoodQuestion(query, mentionedFoods[0], foods, language);
  }

  const results = isNutrientListQuestion(query, requestedNutrient)
    ? rankFoodsByNutrient(foods, requestedNutrient, { lowToHigh: asksForLowValue(query), limit: 6 })
    : searchAssistantFoods(query, foods);

  if (!results.length) {
    return language === "ar"
      ? `لم أجد "${searchTerm || query}" في بيانات الطعام داخل التطبيق. يمكنك البحث في صفحة الأطعمة أو إضافة طعام مخصص يدويًا.`
      : `I could not find "${searchTerm || query}" in the app food data. You can still search the Foods page or add a custom food manually.`;
  }

  const lines = results.map((food) => formatFoodResult(food, language));
  return language === "ar"
    ? ["وجدت هذه الأطعمة في بيانات التطبيق:", ...lines].join("\n")
    : ["I found these foods in the app data:", ...lines].join("\n");
}

export function answerAvailableFoods(context = {}, { language = "en" } = {}) {
  const foods = context.foods || [];
  if (!foods.length) {
    return language === "ar"
      ? "لا توجد أطعمة متاحة في بيانات التطبيق على هذا الجهاز."
      : "No foods are available in the app data on this device.";
  }

  const verified = foods.filter((food) => food.type === "verified").length;
  const custom = foods.filter((food) => food.type === "custom").length;
  const names = foods.slice(0, 10).map(getFoodName).filter(Boolean).join(language === "ar" ? "، " : ", ");

  if (language === "ar") {
    return [
      `قائمة الطعام في التطبيق تحتوي على ${foods.length} أطعمة: ${verified} أطعمة معتمدة و ${custom} أطعمة مخصصة.`,
      names ? `أمثلة: ${names}.` : "",
      "افتح صفحة الأطعمة لتصفح القائمة الكاملة.",
    ]
      .filter(Boolean)
      .join("\n");
  }

  return [
    `The app food list has ${foods.length} foods: ${verified} verified and ${custom} custom.`,
    names ? `Examples: ${names}.` : "",
    "Open Foods to browse the full list.",
  ]
    .filter(Boolean)
    .join("\n");
}

export function answerDashboardQuestion(query = "", context = {}, { language } = {}) {
  const responseLanguage = language || getResponseLanguage(query);
  const cards = context.nutrientCards || [];
  if (!cards.length) {
    return responseLanguage === "ar"
      ? "ملخص اليوم غير متاح في بيانات التطبيق الآن."
      : "Dashboard totals are not available in the app data right now.";
  }

  const requestedNutrient = getRequestedNutrient(query);
  const selectedCards = requestedNutrient ? cards.filter((card) => card.key === requestedNutrient) : cards;
  const summary = context.safetySummary || buildSafetySummary(cards);

  if (responseLanguage === "ar") {
    return [
      "ملخص اليوم من بيانات التطبيق:",
      ...selectedCards.map(
        (card) =>
          `${getArabicNutrientLabel(card.key, card.label)}: تم استخدام ${formatAmount(card.consumed)} ${card.unit}، والمتبقي ${formatAmount(card.remaining)} ${card.unit}، بنسبة ${card.percentage}% من الحد ${formatAmount(card.limit)} ${card.unit}. الحالة: ${getArabicStatusLabel(card.statusLabel)}.`
      ),
      !requestedNutrient ? `ملخص المتابعة: ${summary.title} ${summary.detail}` : "",
    ]
      .filter(Boolean)
      .join("\n");
  }

  return [
    "Today's dashboard summary from app data:",
    ...selectedCards.map(
      (card) =>
        `${card.label}: ${formatAmount(card.consumed)} ${card.unit} used, ${formatAmount(card.remaining)} ${card.unit} remaining, ${card.percentage}% of ${formatAmount(card.limit)} ${card.unit}. Status: ${card.statusLabel}.`
    ),
    !requestedNutrient ? `Safety summary: ${summary.title} ${summary.detail}` : "",
  ]
    .filter(Boolean)
    .join("\n");
}

export function answerHistoryQuestion(query = "", context = {}, { language } = {}) {
  const responseLanguage = language || getResponseLanguage(query);
  const logs = context.foodLogs || [];
  const dateKey = extractDateKey(query) || context.todayKey || getLocalDateKey();

  if (/14-day|14\s+day|last\s+14/i.test(query)) {
    return answerPeriodHistory(logs, context.dailyTargets, 14, responseLanguage);
  }

  if (/30-day|30\s+day|last\s+30/i.test(query)) {
    return answerPeriodHistory(logs, context.dailyTargets, 30, responseLanguage);
  }

  const dayLogs = getLogsForDate(logs, dateKey);
  if (!dayLogs.length) {
    return responseLanguage === "ar"
      ? `لا توجد أطعمة مسجلة في بيانات التطبيق بتاريخ ${dateKey}.`
      : `No food entries are recorded for ${dateKey} in the app data.`;
  }

  const totals = sumNutrients(dayLogs);
  const names = dayLogs.map((log) => `${log.food_name_snapshot || "Food"} (${formatServing(log.quantity, log.quantity_unit)})`);

  if (responseLanguage === "ar") {
    return [`أطعمة ${dateKey}:`, names.join("، "), formatTotalsLine(totals, "ar")].join("\n");
  }

  return [`Food entries for ${dateKey}:`, names.join(", "), formatTotalsLine(totals)].join("\n");
}

export function searchKnowledgeItems(query = "", context = {}, { limit = 5 } = {}) {
  const terms = normalizeSearchTerms(extractKnowledgeSearchTerm(query));
  if (!terms.length) return [];

  return getKnowledgeSources(context)
    .map((item) => ({ item, score: scoreKnowledgeItem(item, terms) }))
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score || a.item.title.localeCompare(b.item.title))
    .slice(0, limit)
    .map((entry) => entry.item);
}

export function buildKnowledgeAnswer(query = "", context = {}) {
  const results = searchKnowledgeItems(query, context);
  const searchTerm = extractKnowledgeSearchTerm(query);

  if (!results.length) {
    return `I could not find approved knowledge content for "${searchTerm || query}" in the app data.`;
  }

  return [
    "Approved knowledge found in the app data:",
    ...results.map((item) => {
      const content = item.content
        ? ` ${truncate(item.content, 180)}`
        : ` File metadata only: ${item.file_name || item.type}.`;
      return `${item.title} (${item.category}).${content}`;
    }),
  ].join("\n");
}

export function buildAppHelpAnswer(query = "") {
  return answerV2AppGuideQuestion(query);
}

export function buildReminderHelpAnswer() {
  return [
    "Medication reminders are managed from the Reminders page.",
    "I can explain the reminder settings, but I cannot create or change reminders for you.",
  ].join("\n");
}

export function sanitizeAssistantText(text = "") {
  return String(text || "")
    .replace(/\bSaved\b/g, "Stored")
    .replace(/\bLogged\b/g, "Recorded")
    .replace(/تم تسجيل/gu, "لا أستطيع تأكيد أي تغيير")
    .trim();
}

function answerPeriodHistory(logs = [], targets = {}, days = 14, language = "en") {
  const periodLogs = filterLogsForLastDays(logs, days);
  if (!periodLogs.length) {
    return language === "ar"
      ? `لا توجد أطعمة مسجلة في بيانات التطبيق خلال آخر ${days} يومًا.`
      : `No food entries are recorded for the last ${days} days in the app data.`;
  }

  const totals = sumNutrients(periodLogs);
  const cards = buildNutrientCards(totals, targets || {});
  const averageUsed = cards.length
    ? Math.round(cards.reduce((sum, card) => sum + card.percentage, 0) / cards.length)
    : 0;

  if (language === "ar") {
    return [
      `آخر ${days} يومًا: تم العثور على ${periodLogs.length} إدخالات طعام في بيانات التطبيق.`,
      formatTotalsLine(totals, "ar"),
      `متوسط استخدام الحدود للعناصر المتابعة: ${averageUsed}%.`,
    ].join("\n");
  }

  return [
    `Last ${days} days: ${periodLogs.length} food entries found in the app data.`,
    formatTotalsLine(totals),
    `Average target use across tracked nutrients: ${averageUsed}%.`,
  ].join("\n");
}

function formatFoodResult(food = {}, language = "en") {
  const name = getFoodName(food);
  const serving = formatServing(food.quantity ?? food.serving_quantity, food.quantity_unit || food.serving_unit);
  if (language === "ar") {
    return `${name} (${food.category || "غير مصنف"}, ${food.type_label || "Food"}): ${serving}؛ البوتاسيوم ${formatAmount(food.potassium_mg)} mg، الفوسفور ${formatAmount(food.phosphorus_mg)} mg، الصوديوم ${formatAmount(food.sodium_mg)} mg، البروتين ${formatAmount(food.protein_g)} g.`;
  }
  return `${name} (${food.category || "Uncategorized"}, ${food.type_label || "Food"}): ${serving}; potassium ${formatAmount(food.potassium_mg)} mg, phosphorus ${formatAmount(food.phosphorus_mg)} mg, sodium ${formatAmount(food.sodium_mg)} mg, protein ${formatAmount(food.protein_g)} g.`;
}

function formatTotalsLine(totals = {}, language = "en") {
  if (language === "ar") {
    return `المجموع: البوتاسيوم ${formatAmount(totals.potassium)} mg، الفوسفور ${formatAmount(totals.phosphorus)} mg، الصوديوم ${formatAmount(totals.sodium)} mg، البروتين ${formatAmount(totals.protein)} g.`;
  }
  return `Totals: potassium ${formatAmount(totals.potassium)} mg, phosphorus ${formatAmount(totals.phosphorus)} mg, sodium ${formatAmount(totals.sodium)} mg, protein ${formatAmount(totals.protein)} g.`;
}

function getKnowledgeSources(context = {}) {
  const knowledgeItems = (context.knowledgeItems || []).map((item) => ({
    id: item.id,
    title: item.title,
    category: item.category,
    type: item.type,
    content: item.content,
    file_name: item.file_name,
    source_notes: item.source_notes,
  }));

  const tips = (context.approvedTips || []).map((tip) => ({
    id: tip.id,
    title: tip.title_en || tip.title || "Approved tip",
    category: tip.category || "Tip",
    type: "approved_tip",
    content: tip.body_en || tip.body || "",
    source_notes: "Approved dashboard tip",
  }));

  return [...knowledgeItems, ...tips].filter((item) => item.title);
}

function scoreFood(food = {}, terms = []) {
  const searchable = [
    getFoodName(food),
    food.display_name_ar,
    food.name_ar,
    food.category,
    food.source,
    food.type_label,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return terms.reduce((score, term) => {
    const aliases = expandFoodSearchTerm(term);
    if (aliases.some((alias) => getFoodName(food).toLowerCase() === alias)) return score + 10;
    return aliases.some((alias) => searchable.includes(alias)) ? score + 2 : score;
  }, 0);
}

function findMentionedFoods(query = "", foods = []) {
  const normalizedQuery = ` ${normalizeMentionText(query)} `;
  const matches = [];

  foods.forEach((food) => {
    getFoodMentionTerms(food).forEach((term) => {
      const normalizedTerm = normalizeMentionText(term);
      if (!normalizedTerm) return;

      const needle = ` ${normalizedTerm} `;
      let fromIndex = 0;
      let matchIndex = normalizedQuery.indexOf(needle, fromIndex);
      while (matchIndex >= 0) {
        matches.push({
          food,
          start: matchIndex,
          end: matchIndex + needle.length,
          length: needle.length,
        });
        fromIndex = matchIndex + 1;
        matchIndex = normalizedQuery.indexOf(needle, fromIndex);
      }
    });
  });

  const selected = [];
  matches
    .sort((a, b) => b.length - a.length || a.start - b.start)
    .forEach((candidate) => {
      const overlaps = selected.some(
        (match) => candidate.start < match.end && candidate.end > match.start
      );
      if (!overlaps) selected.push(candidate);
    });

  return selected
    .sort((a, b) => a.start - b.start)
    .map((match) => match.food)
    .filter((food, index, all) => all.findIndex((item) => item.id === food.id) === index);
}

function getFoodMentionTerms(food = {}) {
  const englishName = getFoodName(food).toLowerCase();
  const names = [getFoodName(food), food.display_name_ar, food.name_ar].filter(Boolean);
  Object.entries(ARABIC_FOOD_ALIASES).forEach(([alias, mappedName]) => {
    if (mappedName === englishName) names.push(alias);
  });
  return [...new Set(names)];
}

function normalizeMentionText(value = "") {
  return String(value || "")
    .toLowerCase()
    .replace(/[\s,.;:!?؟،؛()[\]{}'"-]+/gu, " ")
    .trim();
}

function isDirectFoodQuestion(query = "") {
  return /\b(?:is|are)\b.*\b(?:high|low)\s+in\b|\bhow\s+much\b|\bdoes\b.*\b(?:contain|have)\b|\bwhat\s+(?:nutrients?|nutrition)\b|هل.*(?:غني|غنية|عال|عالي|عالية|منخفض|منخفضة|قليل|قليلة|يحتوي|تحتوي)|كم.*(?:بوتاسيوم|فوسفور|صوديوم|بروتين)|ما\s+(?:هي\s+)?(?:العناصر\s+الغذائية|المغذيات)/iu.test(
    String(query)
  );
}

function isNutrientListQuestion(query = "", nutrient = "") {
  if (!nutrient) return false;
  return /\b(?:show|list|find|suggest)\b.*\b(?:foods?|options?)\b|(?:اعرض|أظهر|اظهر|اقترح).*?(?:أطعمة|اطعمة|خيارات)/iu.test(
    String(query)
  );
}

function asksForLowValue(query = "") {
  return /\blow\b|منخفض|منخفضة|قليل|قليلة/iu.test(String(query));
}

function asksForHighValue(query = "") {
  return /\bhigh\b|غني|غنية|عال|عالي|عالية/iu.test(String(query));
}

function answerSingleFoodQuestion(query, food, foods, language) {
  const nutrient = getRequestedNutrient(query);
  if (!nutrient) {
    return formatSingleFoodNutrition(food, language);
  }

  const details = NUTRIENT_DETAILS[nutrient];
  const amount = Number(food?.[details.field]) || 0;
  const serving = formatServingSubject(food, language);
  const amountSentence = language === "ar"
    ? `تحتوي ${serving} على نحو ${formatAmount(amount)} ${details.unit} من ${details.labelAr}.`
    : `${capitalize(serving)} contains about ${formatAmount(amount)} ${details.unit} of ${details.label}.`;

  if (/\bdoes\b.*\b(?:contain|have)\b|هل.*(?:يحتوي|تحتوي)/iu.test(String(query))) {
    const yes = amount > 0;
    return language === "ar" ? `${yes ? "نعم" : "لا"}. ${amountSentence}` : `${yes ? "Yes" : "No"}. ${amountSentence}`;
  }

  if (asksForHighValue(query) || asksForLowValue(query)) {
    const asksLow = asksForLowValue(query);
    const relativeMatch = isRelativeNutrientMatch(food, foods, nutrient, { low: asksLow });
    const level = asksLow ? (language === "ar" ? "منخفضة نسبيًا" : "relatively low") : (language === "ar" ? "مرتفعة نسبيًا" : "relatively high");
    const comparison = language === "ar"
      ? `${relativeMatch ? "وهي" : "وليست"} ${level} مقارنةً بقيم الأطعمة في التطبيق.`
      : `It is ${relativeMatch ? "" : "not "}${level} compared with values in the app food data.`;
    const guidance = !asksLow && relativeMatch
      ? language === "ar"
        ? `اتبع حد ${details.labelAr} الذي أوصى به فريق رعاية الكلى.`
        : `Follow the ${details.label} limit recommended by your renal care team.`
      : "";
    return [`${relativeMatch ? (language === "ar" ? "نعم" : "Yes") : (language === "ar" ? "لا" : "No")}. ${amountSentence}`, comparison, guidance]
      .filter(Boolean)
      .join(" ");
  }

  return amountSentence;
}

function answerFoodComparison(foods, nutrient, language) {
  if (!nutrient) {
    return foods.map((food) => formatFoodResult(food, language)).join("\n");
  }
  return foods.map((food) => formatFoodNutrientLine(food, nutrient, language)).join("\n");
}

function rankFoodsByNutrient(foods, nutrient, { lowToHigh = false, limit = 6 } = {}) {
  const details = NUTRIENT_DETAILS[nutrient];
  if (!details) return [];
  return foods
    .filter((food) => Number.isFinite(Number(food?.[details.field])))
    .sort((a, b) => {
      const difference = Number(a[details.field]) - Number(b[details.field]);
      return (lowToHigh ? difference : -difference) || getFoodName(a).localeCompare(getFoodName(b));
    })
    .slice(0, limit);
}

function isRelativeNutrientMatch(food, foods, nutrient, { low = false } = {}) {
  const details = NUTRIENT_DETAILS[nutrient];
  const values = foods
    .map((item) => Number(item?.[details.field]))
    .filter(Number.isFinite)
    .sort((a, b) => a - b);
  if (!values.length) return false;
  const percentile = low ? 0.25 : 0.75;
  const threshold = values[Math.round((values.length - 1) * percentile)];
  const amount = Number(food?.[details.field]) || 0;
  return low ? amount <= threshold : amount >= threshold;
}

function formatSingleFoodNutrition(food, language) {
  const serving = formatServingSubject(food, language);
  if (language === "ar") {
    return `تحتوي ${serving} على البوتاسيوم ${formatAmount(food.potassium_mg)} mg، والفوسفور ${formatAmount(food.phosphorus_mg)} mg، والصوديوم ${formatAmount(food.sodium_mg)} mg، والبروتين ${formatAmount(food.protein_g)} g.`;
  }
  return `${capitalize(serving)} contains potassium ${formatAmount(food.potassium_mg)} mg, phosphorus ${formatAmount(food.phosphorus_mg)} mg, sodium ${formatAmount(food.sodium_mg)} mg, and protein ${formatAmount(food.protein_g)} g.`;
}

function formatFoodNutrientLine(food, nutrient, language) {
  const details = NUTRIENT_DETAILS[nutrient];
  const name = getFoodName(food);
  const serving = formatServing(food.quantity ?? food.serving_quantity, food.quantity_unit || food.serving_unit);
  return language === "ar"
    ? `${name}: ${serving}؛ ${details.labelAr} ${formatAmount(food[details.field])} ${details.unit}.`
    : `${name}: ${serving}; ${details.label} ${formatAmount(food[details.field])} ${details.unit}.`;
}

function formatServingSubject(food, language) {
  const name = getFoodName(food);
  const serving = formatServing(food.quantity ?? food.serving_quantity, food.quantity_unit || food.serving_unit);
  if (language === "ar") return `حصة من ${name} (${serving})`;

  const oneServing = serving.match(/^1\s+(.+)$/);
  if (!oneServing) return `a ${serving} serving of ${name}`;

  const unit = oneServing[1];
  if (/^(?:small|medium|large)$/i.test(unit)) return `one ${unit} ${name.toLowerCase()}`;
  return `one ${unit} of ${name.toLowerCase()}`;
}

function capitalize(value = "") {
  return value ? `${value.charAt(0).toUpperCase()}${value.slice(1)}` : "";
}

function scoreKnowledgeItem(item = {}, terms = []) {
  const searchable = [item.title, item.category, item.type, item.content, item.source_notes, item.file_name]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return terms.reduce((score, term) => {
    if (!term) return score;
    if (String(item.title || "").toLowerCase().includes(term)) return score + 5;
    return searchable.includes(term) ? score + 2 : score;
  }, 0);
}

function extractFoodSearchTerm(query = "") {
  const lower = String(query || "").trim().toLowerCase();
  if (/available foods?|foods available|food list|شو في|أطعمة|اطعمة/i.test(lower)) return "";

  return stripPrefixes(lower, FOOD_SEARCH_PREFIXES)
    .replace(/\b(nutrients?|nutrition|serving|values?|for|please|app|food|foods)\b/g, " ")
    .replace(/[?؟]/g, " ")
    .trim();
}

function extractKnowledgeSearchTerm(query = "") {
  return stripPrefixes(String(query || "").trim().toLowerCase(), KNOWLEDGE_PREFIXES)
    .replace(/\b(what|does|say|about|show|search|for|approved|items?)\b/g, " ")
    .replace(/[?؟]/g, " ")
    .trim();
}

function stripPrefixes(value = "", prefixes = []) {
  return prefixes.reduce((current, prefix) => {
    if (current.startsWith(prefix)) return current.slice(prefix.length).trim();
    return current;
  }, value);
}

function normalizeSearchTerms(value = "") {
  return String(value || "")
    .toLowerCase()
    .split(/[\s,.;:!?؟]+/)
    .map((term) => term.trim())
    .filter((term) => term.length >= 2);
}

function expandFoodSearchTerm(term) {
  const normalized = String(term || "").trim().toLowerCase();
  const withoutArabicArticle = normalized.replace(/^ال/u, "");
  return [
    normalized,
    withoutArabicArticle,
    ARABIC_FOOD_ALIASES[normalized],
    ARABIC_FOOD_ALIASES[withoutArabicArticle],
  ].filter(Boolean);
}

function getRequestedNutrient(query = "") {
  const text = String(query || "").toLowerCase();
  if (/potassium|بوتاسيوم/.test(text)) return "potassium";
  if (/phosphorus|فوسفور/.test(text)) return "phosphorus";
  if (/sodium|صوديوم/.test(text)) return "sodium";
  if (/protein|بروتين/.test(text)) return "protein";
  return "";
}

function extractDateKey(query = "") {
  const match = String(query || "").match(/\b\d{4}-\d{2}-\d{2}\b/);
  return match ? match[0] : "";
}

function getFoodName(food = {}) {
  return food.display_name || food.name_en || food.name || "Food";
}

function getResponseLanguage(query = "") {
  return /[\u0600-\u06ff]/.test(String(query)) ? "ar" : "en";
}

function getArabicNutrientLabel(key, fallback) {
  const labels = {
    potassium: "البوتاسيوم",
    phosphorus: "الفوسفور",
    sodium: "الصوديوم",
    protein: "البروتين",
  };
  return labels[key] || fallback;
}

function getArabicStatusLabel(label = "") {
  if (/over/i.test(label)) return "فوق الحد";
  if (/close/i.test(label)) return "قريب من الحد";
  if (/range/i.test(label)) return "ضمن النطاق";
  return label;
}

function formatAmount(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "0";
  if (Number.isInteger(numeric)) return String(numeric);
  return numeric.toFixed(1);
}

function truncate(value = "", maxLength = 180) {
  const text = String(value || "").trim();
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength - 1).trim()}...`;
}
